/**
 * AI-drafted report — data model + mock backend.
 *
 * Inspired by a2zradiology.ai's workflow pattern: the AI writes a
 * *preliminary* structured report, the reading physician accepts /
 * edits / rejects each finding, and the sum becomes the signed
 * report. Measured outcomes in their pilot: reporting time -17.8%,
 * mental demand -22.4%, confidence +14.8%.
 *
 * This module is deliberately decoupled from the real AI service —
 * `fetchReportDraft(studyUid, specialty)` returns mock data today so
 * the UI is usable end-to-end; swap the implementation for an HTTP
 * call when the AI backend comes online in the next phase.
 */

import type { Specialty } from "../specialty/config";

export type FindingSeverity = "critical" | "significant" | "minor" | "normal";

export interface AIFinding {
  id:        string;
  /** Anatomy tag — used for grouping ("Liver", "Mediastinum"). */
  anatomy:   string;
  /** Severity pill displayed next to the row. `critical` matches
   *  a2zradiology.ai's 7 urgent-triage classes (AAA, free air, SBO,
   *  pancreatitis, hydronephrosis, cholecystitis, diverticulitis). */
  severity:  FindingSeverity;
  /** A single plain-English sentence that will go into the report
   *  verbatim when accepted. Keep it crisp — radiologists edit
   *  heavy AI prose anyway; start terse. */
  sentence:  string;
  /** SOP Instance UID of the slide / slice / frame the AI keyed this
   *  finding to. Clicking the finding jumps the viewport there. */
  keyInstanceUid?: string;
  /** Optional frame number inside the instance (multi-frame only). */
  frameNumber?:   number;
  /** Optional bounding box in image pixel space — lets the UI pin
   *  the viewport and draw a hint overlay. */
  bbox?: { x: number; y: number; w: number; h: number };
  /** Model + version that produced this finding. Surfaced in the
   *  audit trail; not shown to the reader by default. */
  model?:    string;
}

export type FindingState =
  | "pending"          // AI proposed, no human action yet
  | "accepted"         // Inserted verbatim into the report
  | "edited"           // Human rewrote the sentence before accepting
  | "rejected";        // Explicitly dismissed

/** Per-study state kept in memory. Persists to the server when the
 *  report is signed; lost on reload for now (same as measurements). */
export interface ReviewedFinding extends AIFinding {
  state:     FindingState;
  /** Human-edited sentence, when state==='edited'. */
  edited?:   string;
}

export interface ReportDraft {
  studyUid:  string;
  specialty: Specialty;
  /** Section headings that structure the human-written prose
   *  around the AI findings. Each template has its own ordering. */
  sections:  Array<{ title: string; template?: string }>;
  findings:  AIFinding[];
  /** Produced-at timestamp (unix seconds) — shown in the panel
   *  header so the reader knows whether the draft is stale. */
  producedAt: number;
}

// --------------------------------------------------------------------
//  Section templates — dictated by specialty.
// --------------------------------------------------------------------

export const SECTION_TEMPLATES: Record<Specialty, Array<{ title: string; template?: string }>> = {
  radiology: [
    { title: "Clinical context",  template: "Clinical indication: " },
    { title: "Technique",         template: "Protocol: " },
    { title: "Comparison",        template: "Prior study: " },
    { title: "Findings",          template: "" },
    { title: "Impression",        template: "1. " },
  ],
  pathology: [
    { title: "Specimen",          template: "Site: " },
    { title: "Clinical history" },
    { title: "Gross description" },
    { title: "Microscopic description" },
    { title: "Ancillary studies",  template: "IHC / ISH: " },
    { title: "Diagnosis",          template: "" },
    { title: "Synoptic summary" },
  ],
  endoscopy: [
    { title: "Indication" },
    { title: "Procedure",         template: "Scope: · Sedation: · Extent: " },
    { title: "Findings" },
    { title: "Interventions",     template: "Biopsies: · Polypectomies: " },
    { title: "Impression" },
    { title: "Recommendations" },
  ],
  radiotherapy: [
    { title: "Prescription",      template: "Dose: · Fractions: · Target: " },
    { title: "Target volumes" },
    { title: "Organs at risk" },
    { title: "Plan quality",      template: "DVH summary: " },
    { title: "Recommendations" },
  ],
};

// --------------------------------------------------------------------
//  Mock backend.  Specialty decides which canned findings show up.
//  Replace with `fetch('/viewer-api/ai/report/' + studyUid)` when the
//  model service ships.
// --------------------------------------------------------------------

const MOCK_RADIOLOGY: AIFinding[] = [
  {
    id: "rad-1", anatomy: "Liver", severity: "significant",
    sentence: "Focal hypoattenuating lesion in hepatic segment VII measuring 2.1 × 1.8 cm; differential includes simple cyst versus cystic metastasis — recommend MR for characterisation.",
    model: "a2z-urgent-v1",
  },
  {
    id: "rad-2", anatomy: "Gallbladder", severity: "minor",
    sentence: "Multiple gallstones within a non-distended, thin-walled gallbladder. No evidence of acute cholecystitis.",
    model: "a2z-urgent-v1",
  },
  {
    id: "rad-3", anatomy: "Kidneys", severity: "normal",
    sentence: "Both kidneys are normal in size and symmetric in enhancement. No hydronephrosis.",
    model: "a2z-urgent-v1",
  },
  {
    id: "rad-4", anatomy: "Aorta", severity: "normal",
    sentence: "Abdominal aorta is normal in calibre; no aneurysm, dissection or mural thrombus.",
    model: "a2z-urgent-v1",
  },
  {
    id: "rad-5", anatomy: "Bowel", severity: "normal",
    sentence: "Bowel loops are of normal calibre; no evidence of small bowel obstruction or free intraperitoneal air.",
    model: "a2z-urgent-v1",
  },
];

const MOCK_PATHOLOGY: AIFinding[] = [
  {
    id: "path-1", anatomy: "Breast (core biopsy)", severity: "critical",
    sentence: "Invasive ductal carcinoma, grade 2, with associated DCIS of intermediate grade. Recommend IHC panel: ER, PR, HER2, Ki-67.",
    model: "vpd-path-stub",
  },
  {
    id: "path-2", anatomy: "Breast — margin", severity: "significant",
    sentence: "Tumour approaches < 1 mm of the inked deep margin; re-excision may be warranted.",
  },
];

const MOCK_ENDOSCOPY: AIFinding[] = [
  {
    id: "endo-1", anatomy: "Sigmoid colon — 35 cm", severity: "significant",
    sentence: "12 mm sessile polyp; ileo-cecal junction reached. Cold-snare polypectomy performed.",
  },
  {
    id: "endo-2", anatomy: "Descending colon — 45 cm", severity: "minor",
    sentence: "Diminutive hyperplastic polyp; biopsy taken.",
  },
];

const MOCK_RT: AIFinding[] = [
  {
    id: "rt-1", anatomy: "PTV_7000", severity: "significant",
    sentence: "PTV coverage V95% = 98.3% (target ≥ 95%); D2% = 7315 cGy within acceptable tolerance.",
  },
  {
    id: "rt-2", anatomy: "Rectum", severity: "minor",
    sentence: "Rectum V70Gy = 11.4% (constraint < 15%).",
  },
];

export async function fetchReportDraft(
  studyUid:  string,
  specialty: Specialty,
): Promise<ReportDraft> {
  // Real-backend path — server.ts mounts a mock on
  // /viewer-api/ai/report/:uid today; the shape matches the real AI
  // service's planned contract exactly, so switching to a forwarding
  // proxy is a single-line server change when the model ships.
  try {
    const url = `/viewer-api/ai/report/${encodeURIComponent(studyUid)}?specialty=${encodeURIComponent(specialty)}`;
    const rsp = await fetch(url, { headers: { accept: "application/json" } });
    if (rsp.ok) return (await rsp.json()) as ReportDraft;
  } catch { /* fall through to local mock */ }

  // Fallback — lets the panel stay useful during dev / offline work.
  const findings = {
    radiology:    MOCK_RADIOLOGY,
    pathology:    MOCK_PATHOLOGY,
    endoscopy:    MOCK_ENDOSCOPY,
    radiotherapy: MOCK_RT,
  }[specialty];
  return {
    studyUid,
    specialty,
    sections: SECTION_TEMPLATES[specialty],
    findings,
    producedAt: Math.floor(Date.now() / 1000),
  };
}
